import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { ClipFilterCheckbox } from './clipFilterCheckbox';

/**
 * One pickable speaker: a person, and every transcript label that resolves to
 * them.
 *
 * Labels are plural because two can map to one person once speakers have been
 * combined, and because a person is also reachable through an
 * `override_<id>` label. Selecting the *person* has to select all of their
 * labels, or a clip stamped with the other one would be filtered out of a list
 * the creator asked to see.
 */
export type ClipSpeakerOption = {
  /** Stable key — the Dato speaker id, or the raw label when unidentified. */
  id: string;
  name: string;
  headshotUrl?: string | null;
  labels: string[];
};

/** Fixed panel width; the trigger is narrow and cannot set it. */
const MENU_WIDTH = 260;

/** Cap before the list scrolls inside itself — a long story has many speakers. */
const MENU_MAX_HEIGHT = 320;

interface ClipSpeakerFilterProps {
  speakers: ClipSpeakerOption[];
  /** The selected people, by option id. */
  selectedIds: string[];
  onApply: (ids: string[]) => void;
}

/**
 * PLAT-11096 — "Speakers" for the clip picker.
 *
 * A sibling of the shared `TopicFilterDropdown`, deliberately the same shape —
 * trigger with a count, portaled checkable list — so the two controls sitting
 * beside each other in the picker read as one filter row rather than two
 * borrowed widgets. It differs only in what a row shows: a face and a name, via
 * the shared `SpeakerAvatar` and the same colour hash the Section rows' face
 * pile uses, so the same person is the same colour in both.
 *
 * `useAnchoredDropdown` positions and dismisses it, and portals it at the
 * documented above-all-modal-backdrops tier — which is what makes it usable from
 * inside the picker dialog, itself inside the clip preview.
 */
export const ClipSpeakerFilter: React.FC<ClipSpeakerFilterProps> = ({
  speakers,
  selectedIds,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { menuRef, style } = useAnchoredDropdown(
    isOpen,
    triggerRef,
    () => setIsOpen(false),
    MENU_MAX_HEIGHT,
    { width: MENU_WIDTH },
  );

  const toggle = (id: string) => {
    onApply(
      selectedIds.includes(id)
        ? selectedIds.filter(selected => selected !== id)
        : [...selectedIds, id],
    );
  };

  return (
    <Container>
      <TriggerButton
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid="clip-filter-speakers"
        $hasSelection={selectedIds.length > 0}
        onClick={() => setIsOpen(open => !open)}
      >
        Speakers{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
      </TriggerButton>

      {isOpen &&
        style &&
        createPortal(
          <Dropdown
            ref={menuRef}
            style={style}
            role="listbox"
            data-testid="clip-filter-speakers-menu"
          >
            <DropdownHeaderRow>
              <DropdownHeader>Select Speakers</DropdownHeader>
              {/* The same affordance the topic dropdown beside it carries, and
                  for the same reason: undoing a multi-select one checkbox at a
                  time is the tedious part. Only while there is something to
                  clear. */}
              {selectedIds.length > 0 && (
                <ClearButton
                  type="button"
                  data-testid="clip-filter-speakers-clear"
                  onClick={() => onApply([])}
                >
                  Clear
                </ClearButton>
              )}
            </DropdownHeaderRow>
            <SpeakerList>
              {speakers.map(speaker => {
                const isSelected = selectedIds.includes(speaker.id);
                return (
                  <SpeakerItem
                    key={speaker.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`clip-filter-speaker-${speaker.id}`}
                    onClick={() => toggle(speaker.id)}
                  >
                    <ClipFilterCheckbox checked={isSelected} />
                    <SpeakerAvatar
                      name={speaker.name}
                      color={getSpeakerColorFromLabel(speaker.id)}
                      headshotUrl={speaker.headshotUrl}
                      size={22}
                    />
                    <SpeakerName>{speaker.name}</SpeakerName>
                  </SpeakerItem>
                );
              })}
            </SpeakerList>
          </Dropdown>,
          document.body,
        )}
    </Container>
  );
};

const Container = styled.div`
  display: inline-block;
`;

/* The topic filter's trigger, on the design system's gold rather than the
   topics-blue exception — the blue belongs to the topic badge, not to filter
   chrome. */
const TriggerButton = styled.button<{ $hasSelection: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--radius-button);
  border: 1px solid
    ${props => (props.$hasSelection ? 'var(--accent)' : 'var(--border)')};
  background-color: var(--surface);
  color: ${props =>
    props.$hasSelection ? 'var(--accent)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const Dropdown = styled.div`
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  box-shadow: var(--elevation-toast);
  overflow-y: auto;
`;

const DropdownHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
`;

const DropdownHeader = styled.div`
  font-size: 11px;
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
`;

/** A text action, not a button: it undoes a selection rather than committing. */
const ClearButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const SpeakerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
`;

const SpeakerItem = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: 5px 6px;
  border: none;
  border-radius: var(--radius-button);
  background: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

const SpeakerName = styled.span`
  font-size: var(--text-sm);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
